import React, { useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import Modal from 'components/Modal';
import Button from 'components/Button';
import MessageBox from 'components/MessageBox';
import CircleCheckbox from 'components/CircleCheckbox';
import TextLabel from 'components/TextLabel/TextLabel';
// utils
import classnames from 'classnames/bind';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// styles
import styles from './AffectedPigGroupsModal.module.scss';

const cn = classnames.bind(styles);

const AffectedPigGroupsModal = ({ closeModal, diagnosisName, isActive, pigGroups, onSubmit }) => {
  const [selectedIds, setSelectedIds] = useState(pigGroups.map(({ id }) => id));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAllSelected = selectedIds.length === pigGroups.length;
  const hasCommingledSelected = pigGroups.some(({ id, commingled }) => commingled && selectedIds.includes(id));

  const toggleGroup = (id) => {
    setSelectedIds((prevIds) => (
      prevIds.includes(id) ? prevIds.filter((prevId) => prevId !== id) : [...prevIds, id]
    ));
  };

  const toggleAll = () => {
    setSelectedIds(isAllSelected ? [] : pigGroups.map(({ id }) => id));
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    onSubmit(selectedIds)
      .then(closeModal)
      .catch((errors) => {
        setIsSubmitting(false);
        toastResponseErrors(errors);
      });
  };

  return (
    <Modal title={<FormattedMessage id="component.affectedPigGroupsModal.title" />}>
      <section className="modal-body">
        <div className="mb-10">
          <FormattedMessage
            id={`component.affectedPigGroupsModal.${isActive ? 'applyText' : 'removeText'}`}
            values={{ name: <span className="semibold">{diagnosisName}</span> }}
          />
        </div>
        {isActive && hasCommingledSelected && (
          <MessageBox type="alert" className="mb-10">
            <FormattedMessage id="component.affectedPigGroupsModal.commingledWarning" />
          </MessageBox>
        )}
        <div className={cn('select-all-line')}>
          <span className={cn('select-all-btn')} onClick={toggleAll}>
            <FormattedMessage id={`general.${isAllSelected ? 'deselectAll' : 'selectAll'}`} />
          </span>
        </div>
        <div className={cn('groups-list')}>
          {pigGroups.map(({ id, name, commingled, farm }) => (
            <div key={`affected-group-${id}`} className={cn('group-line')}>
              <CircleCheckbox
                label={(
                  <span className="semibold">
                    {name}
                    {commingled && (
                      <TextLabel className={cn('commingled-label')}>
                        <FormattedMessage id="general.sourceTypes.commingled" />
                      </TextLabel>
                    )}
                  </span>
                )}
                subLabel={farm ? farm.name : ''}
                checked={selectedIds.includes(id)}
                onChange={() => toggleGroup(id)}
              />
            </div>
          ))}
        </div>
      </section>
      <section className="modal-footer text-right sticky-on-mobile">
        <Button onClick={closeModal}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
        <Button
          primary
          className="ml-15"
          disabled={!selectedIds.length || isSubmitting}
          onClick={handleSubmit}
        >
          <FormattedMessage
            id={`component.affectedPigGroupsModal.${isActive ? 'applyBtn' : 'removeBtn'}`}
            values={{ count: selectedIds.length }}
          />
        </Button>
      </section>
    </Modal>
  );
};

AffectedPigGroupsModal.propTypes = {
  closeModal: T.func.isRequired,
  diagnosisName: T.string.isRequired,
  isActive: T.bool.isRequired,
  pigGroups: T.arrayOf(T.shape({
    id: T.number.isRequired,
    name: T.string,
    commingled: T.bool,
    farm: T.object,
  })).isRequired,
  onSubmit: T.func.isRequired,
};

export default connect(null, { closeModal })(AffectedPigGroupsModal);
